// Feedback runs as the signed-in user. Migration 0017 is the authority:
// reporters read their own reports; only admins can change status or reply.
import { z } from 'zod';
import { getSupabaseRaw } from '@/auth/session';

export const FEEDBACK_KINDS = {
  tune: 'Tune problem',
  bug: 'Bug report',
  general: 'General feedback',
};
export const FEEDBACK_STATUSES = {
  open: 'Open',
  in_progress: 'In progress',
  fixed: 'Fixed',
  wont_fix: 'Won’t fix',
};
export type FeedbackKind = keyof typeof FEEDBACK_KINDS;
export type FeedbackStatus = keyof typeof FEEDBACK_STATUSES;

// PostgreSQL char_length counts code points, not UTF-16 code units. Match it
// when decoding reports submitted by another client (including emoji).
const boundedText = (max: number) => z.string().refine((value) => [...value].length <= max);
const tuneSchema = z
  .object({
    id: boundedText(300).default(''),
    title: boundedText(300),
    composer: boundedText(300).optional(),
    source: boundedText(300).optional(),
    revisionId: boundedText(300).optional(),
    key: boundedText(300).optional(),
    timeSignature: z
      .tuple([z.number().int().min(1).max(128), z.number().int().min(1).max(128)])
      .optional(),
    barCount: z.number().int().min(0).max(100000).default(0),
    bar: z.number().int().min(1).optional(),
    measure: z
      .object({
        form: z.record(z.unknown()),
        parts: z.array(z.object({ name: z.string(), measure: z.record(z.unknown()) })),
      })
      .optional(),
  })
  .strict()
  .refine((t) => t.bar === undefined || t.bar <= t.barCount, 'Choose a bar in this tune.');
const contextSchema = z
  .object({
    route: boundedText(500).default('/'),
    build: boundedText(120).default('unknown'),
    browser: boundedText(1000).default('unknown'),
    tune: tuneSchema.optional(),
  })
  .strict();
export type FeedbackContext = z.infer<typeof contextSchema>;

const reportSchema = z.object({
  id: z.string(),
  reporter: z.string(),
  kind: z.enum(['tune', 'bug', 'general']),
  message: z.string(),
  context: contextSchema,
  status: z.enum(['open', 'in_progress', 'fixed', 'wont_fix']),
  reply: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type FeedbackReport = z.infer<typeof reportSchema>;
type Result = { data: unknown; error: { message: string; code?: string } | null };
type Selection = {
  eq(column: string, value: string): Selection;
  order(column: string, options: { ascending: boolean }): { limit(n: number): Promise<Result> };
};
export interface FeedbackClient {
  rpc(fn: string, args: Record<string, unknown>): Promise<Result>;
  from(table: string): { select(columns: string): Selection };
}
let testClient: FeedbackClient | null = null;
function client(): FeedbackClient {
  const value = testClient ?? (getSupabaseRaw() as FeedbackClient | null);
  if (!value) throw new Error('Sign in to send feedback.');
  return value;
}
function resultData(result: Result): unknown {
  if (result.error) {
    if (['42P01', '42883', 'PGRST202', 'PGRST205'].includes(result.error.code ?? '')) {
      throw new Error('Feedback is being set up. Please try again later.');
    }
    throw new Error(result.error.message);
  }
  return result.data;
}

/** PostgREST can represent a composite return as a singleton row set; accept
 * that and the singular JSON representation used by existing RPC clients. */
function returnedReport(data: unknown): FeedbackReport {
  return reportSchema.parse(Array.isArray(data) && data.length === 1 ? data[0] : data);
}

export async function submitFeedback(
  kind: FeedbackKind,
  message: string,
  context: FeedbackContext,
): Promise<FeedbackReport> {
  const text = message.trim();
  if (!text || text.length > 4000) throw new Error('Describe the problem in 1–4,000 characters.');
  const parsed = contextSchema.safeParse(context);
  if (!parsed.success || (kind === 'tune' && !parsed.data.tune)) {
    throw new Error('The report context is incomplete. Reopen feedback from the tune.');
  }
  if (new TextEncoder().encode(JSON.stringify(parsed.data)).length > 60000) {
    throw new Error(
      'This bar has too much detail to attach. Clear the bar number and describe it in your message.',
    );
  }
  return returnedReport(
    resultData(
      await client().rpc('submit_feedback', {
        p_kind: kind,
        p_message: text,
        p_context: parsed.data,
      }),
    ),
  );
}

/** An admin also filters by their id when viewing their own history. */
export async function listFeedback(reporter?: string): Promise<FeedbackReport[]> {
  let query = client().from('feedback_reports').select('*');
  if (reporter) query = query.eq('reporter', reporter);
  const data = resultData(await query.order('created_at', { ascending: false }).limit(200));
  return z.array(reportSchema).parse(data);
}

export async function updateFeedback(
  id: string,
  status: FeedbackStatus,
  reply: string,
): Promise<FeedbackReport> {
  if (reply.trim().length > 2000) throw new Error('Keep the reply under 2,000 characters.');
  return returnedReport(
    resultData(
      await client().rpc('admin_update_feedback', {
        p_id: id,
        p_status: status,
        p_reply: reply.trim(),
      }),
    ),
  );
}

export function __setFeedbackTestClient(value: FeedbackClient | null): void {
  testClient = value;
}
